import { Modal, Typography } from "@/deps/ui-library";
import { useAccountUsage, useRemoveAccount, useSetAccountActive } from "@/api/queries";
import type { Account } from "@/api/types";
import { referenceKindName } from "@/lib/chart";
import { formatNPR, toNumber } from "@/lib/money";
import { refusalMessage } from "./refusal";

/**
 * Remove (handoff §6.6). Nothing posts to it and nothing names it: confirm, and it comes off the chart.
 * Something does: it can't be removed, and Make inactive hides it from pickers instead. Already inactive:
 * only the explanation, and the way back.
 */
export function RemoveAccountDialog({
  account,
  onClose,
}: {
  account: Account;
  onClose: () => void;
}) {
  const usage = useAccountUsage(account.id);
  const remove = useRemoveAccount();
  const setActive = useSetAccountActive();
  const title = `${account.code} · ${account.name}`;

  // Which dialog it is depends on the answer, so none opens until it arrives: a placeholder dialog
  // swapped for another one a moment later reads as two dialogs.
  if (usage.isPending) return null;
  if (usage.error) {
    return (
      <Modal.View open title={`Remove ${title}?`} onClose={onClose} size="md">
        <Typography.Text muted>
          {refusalMessage(usage.error) ?? "What uses this account couldn't be checked."}
        </Typography.Text>
      </Modal.View>
    );
  }

  const references = usage.data.references;
  const posted = toNumber(usage.data.posted);

  if (references.length === 0 && posted === 0) {
    return (
      <Modal.Confirmation
        rootProps={{ open: true, onOpenChange: (details) => !details.open && onClose() }}
        title={`Remove ${title}?`}
        description="Nothing has posted to it and nothing uses it. It comes off the chart and off its statement line."
        confirmLabel="Remove account"
        variant="danger"
        onConfirm={async () => {
          await remove.mutateAsync(account.id).catch((error: unknown) => {
            throw new Error(refusalMessage(error) ?? "That did not work. Try again.");
          });
        }}
      />
    );
  }

  const inUse = (
    <div className="flex flex-col gap-2">
      {posted !== 0 ? (
        <Typography.Text>{`${formatNPR(usage.data.posted)} has posted to it.`}</Typography.Text>
      ) : null}
      {references.length > 0 ? (
        <>
          <Typography.Text>It is used by:</Typography.Text>
          <ul className="text-foreground m-0 flex flex-col gap-0.5 pl-5 text-sm">
            {references.map((ref) => (
              <li key={`${ref.kind}-${ref.accountId}`}>
                {`${referenceKindName(ref.kind)} · ${ref.uses}`}
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );

  if (!account.isActive) {
    return (
      <Modal.Form
        open
        title="This account can't be removed"
        subtitle={`${title} is already inactive: it is hidden from document and journal pickers and stays on every report.`}
        submitLabel="Make active again"
        cancelLabel="Close"
        busy={setActive.isPending}
        error={refusalMessage(setActive.error)}
        onClose={onClose}
        onSubmit={() => setActive.mutate({ id: account.id, active: true }, { onSuccess: onClose })}
      >
        {inUse}
      </Modal.Form>
    );
  }

  return (
    <Modal.Form
      open
      title="This account can't be removed"
      subtitle={`${title} has postings or is in use. Make it inactive instead: it is hidden from document and journal pickers and stays on every report.`}
      submitLabel="Make inactive"
      busy={setActive.isPending}
      error={refusalMessage(setActive.error)}
      onClose={onClose}
      onSubmit={() => setActive.mutate({ id: account.id, active: false }, { onSuccess: onClose })}
    >
      {inUse}
    </Modal.Form>
  );
}
